import { sharesAdapter } from './sharesAdapter'
import { sharesApi } from './sharesApi'
import {
  AdaptedMarketInfoResponse,
  AdaptedStockDetailsResponse,
  AdaptedStockDTO,
  AdaptedGetShareQuotesApiResponse,
  AdaptedStockHistoricalPricesResponse,
  AdaptedPortfolioPositionApiResponse,
  FetchAllStocksParams,
  FetchStocksCategoriesApiResponse,
  GetPortfolioPositionParams,
  GetShareQuotesParams,
  GetSharePricePerUnitApiResponse,
  GetSharePricePerUnitParams,
  AdaptedPortfolioInfoApiResponse,
  GetStocksCountApiResponse,
  StockDetailsParams,
  StockHistoricalPricesParams,
  OrderPayload,
  SellOrderPayload,
  OrderResponse,
  PaginatedStocksResponse,
} from './sharesTypes'

async function getMarketInfo(): Promise<AdaptedMarketInfoResponse> {
  const response = await sharesApi.fetchMarketInfo()
  return {
    market_info: sharesAdapter.adapterMarketInfo(response.market_info),
  }
}

async function fetchStocksCategories(): Promise<FetchStocksCategoriesApiResponse> {
  return sharesApi.fetchStockCategories()
}

async function fetchAllStocks(params: FetchAllStocksParams): Promise<{
  stocks_data: {
    available_stocks: AdaptedStockDTO[]
    available_stocks_count: number
    next_page_id?: number | null
  }
  next_page_id?: number | null
}> {
  const response = await sharesApi.fetchStocks(params)

  return {
    ...response,
    stocks_data: {
      ...response.stocks_data,
      available_stocks: response.stocks_data.available_stocks.map(
        sharesAdapter.adapterStock,
      ),
    },
  }
}

async function fetchMyStocks(
  params: FetchAllStocksParams,
): Promise<Omit<PaginatedStocksResponse, 'stocks'> & { stocks: AdaptedStockDTO[] }> {
  const response = await sharesApi.fetchMyStocks(params)

  return {
    ...response,
    stocks: response.stocks.map(sharesAdapter.adapterStock),
  }
}

async function getStockDetails(
  params: StockDetailsParams,
): Promise<AdaptedStockDetailsResponse> {
  const response = await sharesApi.fetchStockDetails(params)

  return {
    ...response,
    stock_info: sharesAdapter.adapterStock(response.stock_info),
  }
}

async function fetchStockHistoricalPrices(
  params: StockHistoricalPricesParams,
): Promise<AdaptedStockHistoricalPricesResponse> {
  const response = await sharesApi.fetchStockHistoricalPrices(params)

  return {
    ...response,
    stock_prices: sharesAdapter.adapterStockPrices(
      response.stock_prices ?? [],
      params.timespan,
    ),
  }
}

async function getStocksCount(): Promise<GetStocksCountApiResponse> {
  return sharesApi.getStocksCount()
}

async function getPortfolioInfo(): Promise<AdaptedPortfolioInfoApiResponse> {
  const response = await sharesApi.getPortfolioInfo()
  return {
    portfolio_info: sharesAdapter.adapterPortfolioInfo(
      response.portfolio_info,
    ),
  }
}

async function getPortfolioPosition(
  params: GetPortfolioPositionParams,
): Promise<AdaptedPortfolioPositionApiResponse> {
  const response = await sharesApi.getPortfolioPosition(params)
  return {
    portfolio_position: sharesAdapter.adapterPortfolioPosition(
      response.portfolio_position,
    ),
  }
}

async function getSharePricePerUnit(
  params: GetSharePricePerUnitParams,
): Promise<GetSharePricePerUnitApiResponse> {
  return sharesApi.getSharePricePerUnit(params)
}

async function getShareQuotes(
  params: GetShareQuotesParams,
): Promise<AdaptedGetShareQuotesApiResponse> {
  const response = await sharesApi.getShareQuotes(params)
  return sharesAdapter.adapterShareQuotes(response)
}

async function buyStock(payload: OrderPayload): Promise<OrderResponse> {
  return sharesApi.createBuyOrder(payload)
}

async function sellStock(payload: SellOrderPayload): Promise<OrderResponse> {
  return sharesApi.createSellOrder(payload)
}

export const sharesServices = {
  fetchAllStocks,
  fetchStocksCategories,
  getMarketInfo,
  getStockDetails,
  fetchMyStocks,
  fetchStockHistoricalPrices,
  getStocksCount,
  getPortfolioInfo,
  getPortfolioPosition,
  getSharePricePerUnit,
  getShareQuotes,
  buyStock,
  sellStock,
}
